import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  NbButtonModule,
  NbCardModule,
  NbDialogRef,
  NbDialogService,
  NbFormFieldModule,
  NbIconModule,
  NbInputModule,
  NbSelectModule,
  NbSpinnerModule,
} from '@nebular/theme';
import { finalize } from 'rxjs/operators';
import { PreInscricaoInscrito } from '../../../core/api/pre-inscricao.dto';
import { PreInscricaoService } from '../../../core/api/pre-inscricao.service';
import { formatarDataHoraLocal } from '../../../utils/formatar-data-hora-local';
import { mensagemDeErro } from '../../../utils/mensagem-erro';
import { contemNormalizado } from '../../../utils/normalizar-texto';
import { EfetivarMatriculaDialogComponent } from './efetivar-matricula.dialog';

/** Tamanho de página usado para trazer a campanha inteira de uma vez (ver `TAB-11` abaixo). */
const TUDO = 500;

/**
 * Quem se pré-inscreveu numa campanha, com filtro por grupo e por nome, e a efetivação da
 * matrícula.
 *
 * `TAB-11` — **exceção declarada**: o filtro acontece em memória. `GET /pre-inscricoes/{id}/inscritos`
 * só aceita `page` e `size`; não há parâmetro de busca nem de grupo no contrato. Para o filtro não
 * mentir (recortar só a página corrente), a tela traz a campanha inteira numa página de 500 e
 * filtra sobre ela. Campanha maior que isso precisa do filtro no servidor — registrado em
 * `PEND-10`.
 */
@Component({
  selector: 'app-pre-inscricao-inscritos-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NbCardModule,
    NbButtonModule,
    NbFormFieldModule,
    NbIconModule,
    NbInputModule,
    NbSelectModule,
    NbSpinnerModule,
  ],
  templateUrl: './pre-inscricao-inscritos.dialog.html',
  styleUrl: './pre-inscricao-inscritos.dialog.scss',
})
export class PreInscricaoInscritosDialogComponent implements OnInit {
  @Input() campanhaId = '';
  @Input() campanhaNome?: string;

  inscritos: PreInscricaoInscrito[] = [];
  carregando = false;
  erro: string | null = null;

  /** Filtros aplicados — `FIL-01`: valem quando a pessoa manda, não enquanto digita. */
  grupoFiltro = '';
  nomeFiltro = '';
  private nomeAplicado = '';

  /** Matriculados nesta sessão. O contrato não tem onde gravar "pré-inscrição efetivada". */
  private efetivados = new Set<string>();

  /** Id do inscrito cuja confirmação está sendo reenviada agora. */
  reenviandoId: string | null = null;

  /** Resultado do último reenvio, por inscrito: para quem foi, ou que não havia e-mail. */
  resultadoReenvio = new Map<string, string>();

  constructor(
    private ref: NbDialogRef<PreInscricaoInscritosDialogComponent>,
    private service: PreInscricaoService,
    private dialogService: NbDialogService,
  ) {}

  ngOnInit(): void {
    this.carregar();
  }

  carregar(): void {
    if (!this.campanhaId) return;
    this.carregando = true;
    this.erro = null;
    this.service
      .listarInscritos(this.campanhaId, 0, TUDO)
      .pipe(finalize(() => (this.carregando = false)))
      .subscribe({
        next: (pagina) => (this.inscritos = pagina.content ?? []),
        error: (err) => {
          this.inscritos = [];
          this.erro = mensagemDeErro(err, 'Não foi possível carregar os pré-inscritos.');
        },
      });
  }

  /** Grupos presentes na lista, para o seletor — vêm dos próprios inscritos. */
  get gruposDisponiveis(): string[] {
    const nomes = new Set<string>();
    for (const i of this.inscritos) {
      if (i.grupoNome) nomes.add(i.grupoNome);
    }
    return [...nomes].sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }));
  }

  get exibidos(): PreInscricaoInscrito[] {
    return this.inscritos.filter((i) => {
      const casaGrupo = !this.grupoFiltro || i.grupoNome === this.grupoFiltro;
      // `FRM-03` — nome sem acento e sem caixa.
      const casaNome = !this.nomeAplicado || contemNormalizado(i.nomeCatequizando, this.nomeAplicado);
      return casaGrupo && casaNome;
    });
  }

  get temFiltro(): boolean {
    return !!this.grupoFiltro || !!this.nomeAplicado;
  }

  /** `FIL-01` — Enter ou botão. O seletor de grupo aplica na hora, porque escolher já é mandar. */
  buscarAgora(): void {
    this.nomeAplicado = this.nomeFiltro.trim();
  }

  limparFiltro(): void {
    this.grupoFiltro = '';
    this.nomeFiltro = '';
    this.nomeAplicado = '';
  }

  jaEfetivado(i: PreInscricaoInscrito): boolean {
    return !!i.id && this.efetivados.has(i.id);
  }

  /**
   * A pré-inscrição não vira matrícula sozinha: o que existe é a pessoa já cadastrada, e é ela
   * que se matricula numa turma. Sem `pessoaId` não há o que matricular.
   */
  efetivar(i: PreInscricaoInscrito): void {
    if (!i.pessoaId) return;

    this.dialogService
      .open(EfetivarMatriculaDialogComponent, {
        context: { pessoaId: i.pessoaId, pessoaNome: i.nomeCatequizando ?? 'Catequizando' },
      })
      .onClose.subscribe((matriculou) => {
        if (matriculou && i.id) this.efetivados.add(i.id);
      });
  }

  /**
   * Reenvia a confirmação pelo caminho do envio automático: todos os e-mails da ficha, e cada
   * um vira registro em Mensagens. Não é o reenvio de diagnóstico da tela de mensagens.
   *
   * O serviço de e-mail não confirma entrega, então o texto diz **postada**, e para quem.
   */
  reenviarConfirmacao(i: PreInscricaoInscrito): void {
    if (!i.id || this.reenviandoId) return;

    this.reenviandoId = i.id;
    this.service
      .reenviarConfirmacao(this.campanhaId, i.id)
      .pipe(finalize(() => (this.reenviandoId = null)))
      .subscribe({
        next: (r) => {
          const destinos = r.destinatarios ?? [];
          this.resultadoReenvio.set(
            i.id!,
            destinos.length
              ? `Confirmação postada para ${destinos.join(', ')}. O serviço de e-mail não confirma entrega.`
              : 'Esta pré-inscrição não informou e-mail: nada foi enviado.',
          );
        },
        error: (err) =>
          this.resultadoReenvio.set(
            i.id!,
            mensagemDeErro(err, 'Não foi possível reenviar a confirmação.'),
          ),
      });
  }

  resultadoDe(i: PreInscricaoInscrito): string | null {
    return (i.id && this.resultadoReenvio.get(i.id)) || null;
  }

  /** Horário em que a API pôs o inscrito; sem fuso, então lido como texto. */
  atendimento(i: PreInscricaoInscrito): string {
    return formatarDataHoraLocal(i.agendaDataHora);
  }

  telefone(i: PreInscricaoInscrito): string {
    const d = i.dados ?? {};
    const valor = d['celularCatequizando'] ?? d['telefoneMae'] ?? d['telefonePai'];
    return typeof valor === 'string' && valor.trim() ? valor : '—';
  }

  fechar(): void {
    this.ref.close();
  }
}
